import React, { useContext, useEffect, useState } from 'react'

import buttons from '../../../images/icon_button.png'
import joysticks from '../../../images/icon_joystick.png'
import hybrid_hard from '../../../images/icon_hybrid_hard.png'
import touch from '../../../images/icon_touch_hard.png'

import { Box, IconButton } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import BatteryFullIcon from '@mui/icons-material/BatteryFull'
import Battery3BarIcon from '@mui/icons-material/Battery3Bar'
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert'

import { useIsSmallScreen } from '../../../hooks/useIsSmallScreen'
import { useChangeVolume } from '../../../mqtt/mqttHandlerContext'
import { InstrumentType, SONG_TYPES, Song, Instrument, TouchMode } from '../../../utils/types'
import { Drawer } from '../Welcome'
import Text from '../../../assets/components/Text'
import InstrumentSettings from '../Components/InstrumentSettings'
import { useCurrentUser } from '../../../hooks/contexts/currentUserContext'
import Funkingen from '../../../assets/icons/funkingen_2.png'
import InstrumentInformation from './Components/InstrumentInformation'
import { VolumeIcon } from '../../../components/DynamicVolumeIcon'
import { PolyglotContext } from '../../../polyglot/polyGlotContext'
import { mockConnectedInstruments } from '../../../mqtt/mqttHelpers'

export const VOLUME_MIN = 0
export const VOLUME_MAX = 0.1
export const VOLUME_STEPS = 11
export const VOLUME_STEP = (VOLUME_MAX - VOLUME_MIN) / (VOLUME_STEPS - 1)
export const VOLUME_MARKS = Array.from({ length: VOLUME_STEPS }, (_, i) => ({
    value: parseFloat((VOLUME_MIN + i * VOLUME_STEP).toFixed(3))
}))

function handleImageSource(type: InstrumentType, touchMode?: TouchMode): string {
    switch (type) {
        case 'buttons':
            return buttons
        case 'touch':
            return touch
        case 'joysticks':
            return joysticks
        case 'hybrid':
            return hybrid_hard
        default:
            return buttons
    }
}

function getBatteryPercentage(voltage?: number) {
    if (voltage === undefined || voltage === null || Number.isNaN(voltage)) return null
    const minVoltage = 13.4
    const maxVoltage = 16.4
    const percentage = ((voltage - minVoltage) / (maxVoltage - minVoltage)) * 100
    return Math.max(0, Math.min(100, Math.round(percentage)))
}

export function getBatteryStatus(voltage?: number) {
    const percentage = getBatteryPercentage(voltage)

    if (percentage === null) return { percentage: null, icon: null }
    if (percentage > 66) return { percentage, icon: <BatteryFullIcon sx={{ fontSize: 15 }} color="success" /> }
    if (percentage > 25) return { percentage, icon: <Battery3BarIcon sx={{ fontSize: 15 }} color="warning" /> }

    return { percentage, icon: <BatteryAlertIcon sx={{ fontSize: 15 }} color="error" /> }
}

interface InstrumentsProps {
    currentSong: Song | undefined
    instrumentVolumes: { [key: string]: number }
    setInstrumentVolumes: React.Dispatch<React.SetStateAction<{ [key: string]: number }>>
    connectedInstruments: Instrument[]
}

const Instruments = ({ currentSong, instrumentVolumes, setInstrumentVolumes, connectedInstruments }: InstrumentsProps) => {
    const { polyglot } = useContext(PolyglotContext)
    const [isOpen, setIsOpen] = useState(false)
    const [selectedInstrument, setSelectedInstrument] = useState<Instrument>({
        deviceId: '',
        type: '',
        serialNr: '',
        batteryLevel: 0,
        volume: 0,
        arrangement: ''
    })

    const changeVolume = useChangeVolume()
    const isSmallScreen = useIsSmallScreen()
    const currentUser = useCurrentUser()
    const songList = currentUser?.songList
    const shouldShowSettings = currentSong?.type === SONG_TYPES.KEY

    const toggleDrawer = ({ instrumentId, instrumentType, instrumentSerialNr }: { instrumentId?: string; instrumentType?: InstrumentType; instrumentSerialNr?: string; color?: string }) => {
        setIsOpen(!isOpen)
        setSelectedInstrument({
            deviceId: instrumentId ?? '',
            type: instrumentType ?? '',
            serialNr: instrumentSerialNr ?? '',
            batteryLevel: 0,
            volume: 0,
            arrangement: ''
        })
    }

    const handleVolumeStep = (instrument: Instrument, direction: -1 | 1) => {
        const currentVolume = instrumentVolumes[instrument.deviceId] ?? VOLUME_MARKS[1].value
        const currentStep = Math.round((currentVolume - VOLUME_MIN) / VOLUME_STEP)
        const newStep = Math.max(0, Math.min(VOLUME_STEPS - 1, currentStep + direction))
        const newVolume = parseFloat((VOLUME_MIN + newStep * VOLUME_STEP).toFixed(3))

        setInstrumentVolumes((prev) => ({ ...prev, [instrument.deviceId]: newVolume }))
        changeVolume({ volume: newVolume, deviceId: instrument.deviceId })
    }

    useEffect(() => {
        const storedVolumesString = localStorage.getItem('instrumentVolumes')
        if (storedVolumesString) setInstrumentVolumes(JSON.parse(storedVolumesString))
    }, [])

    useEffect(() => {
        localStorage.setItem('instrumentVolumes', JSON.stringify(instrumentVolumes))
    }, [instrumentVolumes])

    if (!connectedInstruments || connectedInstruments.length === 0) {
        return (
            <Box
                sx={{
                    height: '20%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isSmallScreen ? 'left' : 'center',
                    paddingRight: isSmallScreen ? 0 : '10em'
                }}
            >
                <img src={Funkingen} alt="Funkingen" style={{ height: '100%' }} />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Text italic size="heading" textAlign="center">
                        {polyglot.t('home.instruments.headingWaiting')}
                    </Text>
                </Box>
            </Box>
        )
    }

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 1fr',
                width: '100%',
                height: isSmallScreen ? 'auto' : '100%',
                rowGap: isSmallScreen ? '12px' : '4rem',
                p: isSmallScreen ? '8px' : '16px',
                boxSizing: 'border-box',
                alignContent: 'start'
            }}
        >
            {connectedInstruments.map((instrument: Instrument, idx: number) => {
                const currentVolume = instrumentVolumes[instrument.deviceId] ?? VOLUME_MARKS[1].value
                const currentStep = Math.round((currentVolume - VOLUME_MIN) / VOLUME_STEP)
                const batteryStatus = getBatteryStatus(instrument.batteryLevel)

                return (
                    <Box
                        key={idx}
                        sx={{
                            display: isSmallScreen ? 'flex' : 'grid',
                            flexDirection: isSmallScreen ? 'column' : undefined,
                            gridTemplateColumns: isSmallScreen ? undefined : '195px 1fr',
                            gridTemplateRows: isSmallScreen ? undefined : 'auto 1fr auto',
                            columnGap: isSmallScreen ? 0 : '16px',
                            width: '100%',
                            minWidth: 0,
                            bgcolor: '#fff',
                            borderRadius: '10px',
                            p: isSmallScreen ? '8px' : '16px',
                            boxSizing: 'border-box'
                        }}
                    >
                        {/* MOBILE */}
                        {isSmallScreen ? (
                            <>
                                {/* Sound button */}
                                {songList && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: '8px' }}>
                                        <InstrumentInformation isSmallScreen={isSmallScreen} instrument={instrument} songList={songList} shouldShowSettings={shouldShowSettings} currentSong={currentSong} />
                                    </Box>
                                )}

                                {/* Instrument + volume */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                        minWidth: 0
                                    }}
                                >
                                    {/* Instrument */}
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            width: '250px',
                                            flexShrink: 0,
                                            display: 'flex',
                                            justifyContent: 'flex-start',
                                            alignItems: 'center',
                                            overflow: 'hidden',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        <img
                                            src={handleImageSource(instrument.type, instrument.touchMode)}
                                            alt="Bild på instrument"
                                            style={{
                                                display: 'block',
                                                width: '250px',
                                                height: 'auto',
                                                objectFit: 'contain'
                                            }}
                                        />

                                        {/* Battery */}
                                        {instrument.protocol !== '1.0' && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: '5px',
                                                    left: '5px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '3px',
                                                    px: '1rem',
                                                    py: '1rem',
                                                    borderRadius: '4px'
                                                }}
                                            >
                                                {batteryStatus.icon}
                                                <Text size="tiny">{batteryStatus.percentage !== null ? `${batteryStatus.percentage}%` : '-'}</Text>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Mobile volume */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            flexDirection: 'column-reverse',
                                            gap: '5px',
                                            flex: 1,
                                            minWidth: 0,
                                            ml: '8px'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 20,
                                                flexShrink: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <VolumeIcon value={currentVolume} />
                                        </Box>

                                        <IconButton
                                            aria-label="Lower volume"
                                            onClick={() => handleVolumeStep(instrument, -1)}
                                            disabled={currentStep === 0}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                flexShrink: 0,
                                                bgcolor: '#f7f6f4',
                                                '&:hover': { bgcolor: '#efeeeb' }
                                            }}
                                        >
                                            <RemoveIcon sx={{ fontSize: 18 }} />
                                        </IconButton>

                                        <Box
                                            sx={{
                                                width: '22px',
                                                flexShrink: 0,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Text size="small" bold>
                                                {currentStep.toString()}
                                            </Text>
                                        </Box>

                                        <IconButton
                                            aria-label="Raise volume"
                                            onClick={() => handleVolumeStep(instrument, 1)}
                                            disabled={currentStep === VOLUME_STEPS - 1}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                flexShrink: 0,
                                                bgcolor: '#f7f6f4',
                                                '&:hover': { bgcolor: '#efeeeb' }
                                            }}
                                        >
                                            <AddIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </>
                        ) : (
                            <>
                                {/* DESKTOP IMAGE */}
                                <Box
                                    sx={{
                                        gridColumn: '1',
                                        gridRow: '1 / 4',
                                        width: '100%',
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        borderRadius: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative'
                                    }}
                                >
                                    <img
                                        src={handleImageSource(instrument.type, instrument.touchMode)}
                                        alt="Bild på instrument"
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            height: '100%',
                                            maxHeight: '208px',
                                            objectFit: 'contain'
                                        }}
                                    />

                                    {/* Battery */}
                                    {instrument.protocol !== '1.0' && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                bottom: '10px',
                                                left: '10px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                px: '5px',
                                                py: '3px',
                                                borderRadius: '4px'
                                            }}
                                        >
                                            {batteryStatus.icon}
                                            <Text size="tiny">{batteryStatus.percentage !== null ? `${batteryStatus.percentage}%` : '-'}</Text>
                                        </Box>
                                    )}
                                </Box>

                                {/* DESKTOP SOUND BUTTON */}
                                {songList && (
                                    <Box
                                        sx={{
                                            gridColumn: '2',
                                            gridRow: '1',
                                            display: 'flex',
                                            justifyContent: 'flex-start',
                                            alignItems: 'flex-start'
                                        }}
                                    >
                                        <InstrumentInformation instrument={instrument} songList={songList} shouldShowSettings={shouldShowSettings} isSmallScreen={isSmallScreen} currentSong={currentSong} />
                                    </Box>
                                )}

                                {/* DESKTOP VOLUME */}
                                <DesktopVolumeBar currentVolume={currentVolume} currentStep={currentStep} handleVolumeStep={handleVolumeStep} instrument={instrument} />
                            </>
                        )}

                        <Drawer variant="bottom" isOpen={isOpen} toggleDrawer={() => toggleDrawer({})} heading={polyglot.t('home.instrumentInformation.headingChooseSound')}>
                            <InstrumentSettings currentSong={currentSong} selectedInstrument={selectedInstrument} />
                        </Drawer>
                    </Box>
                )
            })}
        </Box>
    )
}

const DesktopVolumeBar = ({ currentVolume, currentStep, handleVolumeStep, instrument }: { currentVolume: number; currentStep: number; handleVolumeStep: any; instrument: Instrument }) => {
    return (
        <Box
            sx={{
                gridColumn: '1/3',
                gridRow: '4',
                display: 'flex',
                alignItems: 'center',
                width: '80%',
                gap: '10px',
                minWidth: 0,
                boxSizing: 'border-box',
                mt: '0.5rem'
            }}
        >
            <Box
                sx={{
                    width: 20,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <VolumeIcon value={currentVolume} />
            </Box>

            <IconButton
                aria-label="Lower volume"
                onClick={() => handleVolumeStep(instrument, -1)}
                disabled={currentStep === 0}
                sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    bgcolor: '#f7f6f4',
                    '&:hover': { bgcolor: '#efeeeb' }
                }}
            >
                <RemoveIcon sx={{ fontSize: 22 }} />
            </IconButton>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flex: 1,
                    minWidth: 0
                }}
            >
                {VOLUME_MARKS.map((mark, index) => (
                    <Box
                        key={mark.value}
                        sx={{
                            width: 12,
                            height: 8,
                            flexShrink: 0,
                            borderRadius: '4px',
                            bgcolor: index <= currentStep ? '#e85e46' : '#deddd9'
                        }}
                    />
                ))}
            </Box>

            <IconButton
                aria-label="Raise volume"
                onClick={() => handleVolumeStep(instrument, 1)}
                disabled={currentStep === VOLUME_STEPS - 1}
                sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    bgcolor: '#f7f6f4',
                    '&:hover': { bgcolor: '#efeeeb' }
                }}
            >
                <AddIcon sx={{ fontSize: 22 }} />
            </IconButton>
        </Box>
    )
}

export default Instruments
